/**
 * Brand asset resolution for frontend-login (ZET-4554 CDN POC).
 *
 * Logo priority:
 * 1. REACT_APP_BRAND_LOGO_URL — absolute override
 * 2. {REACT_APP_BRAND_CDN_BASE}/{orgId}/logo.svg
 * 3. Bundled Zinier wordmark
 *
 * Favicon: {CDN_BASE}/{orgId}/favicon.svg (probed; else default /favicon.ico)
 * Brand display name / welcome copy is out of scope (ZET-4712).
 */
import defaultLogo from '../features/Logo/zinier-logo-with-name-black.svg';

export const CDN_LOGO_FILE = 'logo.svg';
export const CDN_FAVICON_FILE = 'favicon.svg';

export const getDefaultBrandLogo = () => defaultLogo;

export const getBrandCdnBase = () => {
  const base = process.env.REACT_APP_BRAND_CDN_BASE;
  if (!base) {
    return null;
  }
  return String(base).replace(/\/$/, '');
};

/**
 * Build a CDN asset URL for an org.
 * @param {string} orgId
 * @param {string} assetFile e.g. logo.svg
 * @returns {string|null}
 */
export const buildCdnAssetUrl = (orgId, assetFile) => {
  const base = getBrandCdnBase();
  const trimmedOrg = orgId && String(orgId).trim();
  if (!base || !trimmedOrg || !assetFile) {
    return null;
  }
  return `${base}/${encodeURIComponent(trimmedOrg)}/${assetFile}`;
};

const resolveConfiguredLogoUrl = (orgId) => {
  const envLogo = process.env.REACT_APP_BRAND_LOGO_URL;
  if (envLogo) {
    return envLogo;
  }
  return buildCdnAssetUrl(orgId, CDN_LOGO_FILE);
};

/**
 * Resolve the logo URL/src for login screens.
 * @param {string} [orgId]
 * @returns {string}
 */
export const getBrandLogoSrc = (orgId) => {
  return resolveConfiguredLogoUrl(orgId) || defaultLogo;
};

/**
 * Resolve favicon candidate URL (may still 404 — callers should probe).
 * @param {string} [orgId]
 * @returns {string|null}
 */
export const getBrandFaviconSrc = (orgId) => {
  const envFavicon = process.env.REACT_APP_BRAND_FAVICON_URL;
  if (envFavicon) {
    return envFavicon;
  }
  return buildCdnAssetUrl(orgId, CDN_FAVICON_FILE) ||
    resolveConfiguredLogoUrl(orgId);
};

export const DEFAULT_FAVICON_HREF = `${process.env.PUBLIC_URL || ''}/favicon.ico`;

let faviconRequestId = 0;

export const resetFaviconRequestId = () => {
  faviconRequestId = 0;
};

const nextFaviconRequestId = () => {
  faviconRequestId = faviconRequestId + 1;
  return faviconRequestId;
};

const isLatestFaviconRequest = (requestId) => requestId === faviconRequestId;

const setDocumentFavicon = (href) => {
  const existing = document.querySelectorAll('link[rel*=\'icon\']');
  existing.forEach((node) => node.parentNode && node.parentNode.removeChild(node));

  const link = document.createElement('link');
  link.rel = 'icon';
  const sep = href.includes('?') ? '&' : '?';
  link.href = `${href}${sep}v=${Date.now()}`;
  document.head.appendChild(link);
};

const probeImageUrl = (url) => new Promise((resolve) => {
  if (!url || typeof Image === 'undefined') {
    resolve(false);
    return;
  }
  const img = new Image();
  img.onload = () => resolve(true);
  img.onerror = () => resolve(false);
  img.src = url;
});

const resolveFaviconHref = async (orgId) => {
  const candidate = getBrandFaviconSrc(orgId);
  if (!candidate || candidate === defaultLogo) {
    return DEFAULT_FAVICON_HREF;
  }
  const loaded = await probeImageUrl(candidate);
  return loaded ? candidate : DEFAULT_FAVICON_HREF;
};

/**
 * Force default Zinier favicon and cancel in-flight brand probes.
 */
export const resetBrandFavicon = () => {
  if (typeof document === 'undefined') {
    return;
  }
  nextFaviconRequestId();
  setDocumentFavicon(DEFAULT_FAVICON_HREF);
};

/**
 * Apply brand favicon when the asset loads; otherwise restore default.
 * @param {string} [orgId]
 * @returns {Promise<void>}
 */
export const applyBrandFavicon = async (orgId) => {
  if (typeof document === 'undefined') {
    return;
  }
  const requestId = nextFaviconRequestId();
  const href = await resolveFaviconHref(orgId);
  if (!isLatestFaviconRequest(requestId)) {
    return;
  }
  setDocumentFavicon(href);
};
